import { useMemo } from 'react';

import type { CurrencyManager } from '@ctct/application-layer';
import type { BillingStrategyQuery } from '@ctct/domain-layer';
import type { CurrencyPageStore, CurrencyHooks } from '@ctct/state-client-layer';
import { useAvailableCurrencies, useBillingAccount, useUpdateCurrency } from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';
import { useIsCoreMutationLockedAccountState } from '../helpers/useIsSingleBillChildAccount';

interface Props {
  initializer: Pick<CurrencyManager, 'currencyInit'>;
  pageStore: CurrencyPageStore;
  flags?: Parameters<CurrencyManager['currencyInit']>[0]['flags'];
  hooks: CurrencyHooks;
  billingStrategy: BillingStrategyQuery;
}

export function useCurrencyWidget({ initializer, pageStore, flags = [], hooks, billingStrategy }: Props) {
  const availableCurrenciesQuery = useAvailableCurrencies();
  const billingAccountQuery = useBillingAccount();

  const updateCurrencyMutation = useUpdateCurrency();

  const [skipInitializationFromUrl] = flags;
  const { isLocked: isCoreMutationLockedAccount, isPending: isCoreMutationLockPending } =
    useIsCoreMutationLockedAccountState({ billingStrategy });
  const isConfirmedCoreMutationLockedAccount = Boolean(isCoreMutationLockedAccount && !isCoreMutationLockPending);

  const componentProps = useMemo(
    () => ({
      ...initializer.currencyInit({
        pageStore,
        queries: [availableCurrenciesQuery, billingAccountQuery],
        mutations: [updateCurrencyMutation],
        flags: [skipInitializationFromUrl, isConfirmedCoreMutationLockedAccount, Boolean(isCoreMutationLockPending)],
      }),
      hooks,
      isSingleBillChildAccount: Boolean(isCoreMutationLockedAccount),
    }),
    [
      availableCurrenciesQuery,
      billingAccountQuery,
      updateCurrencyMutation,
      initializer,
      pageStore,
      skipInitializationFromUrl,
      hooks,
      isCoreMutationLockedAccount,
      isCoreMutationLockPending,
      isConfirmedCoreMutationLockedAccount,
    ]
  );

  useWidgetLifecycle(componentProps.status, componentProps.lifecycle, isCoreMutationLockPending);

  return componentProps;
}

export type CurrencyWidgetProps = ReturnType<typeof useCurrencyWidget>;
